import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, ThumbsUp, ThumbsDown, Clock, Send, Pin, LogIn, Sparkles, AlertTriangle, X, Reply } from 'lucide-react';
import { ContentModerationActions, SoftDeletePlaceholder } from './ModeratorControls';
import AuthCTA from './AuthCTA';
import EmptyState from './EmptyState';
import LiveSyncIndicator from './LiveSyncIndicator';
import InteractionTip from './InteractionTip';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Link } from 'react-router-dom';
import { cn, normalizeSpeakers } from '../utils';
import { api } from '../api';
import usePolling from '../hooks/usePolling';
import useRateLimit from '../hooks/useRateLimit';
import ConfirmModal from './ConfirmModal';

import { getTheme } from '../utils/themes';

const LiveQA = ({ sessionState = 'live', isModerator = false, sessionId, communityId, roomData = null }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const theme = getTheme(roomData?.type || 'qa');
  const sessionSpeakers = normalizeSpeakers(roomData?.speakers || roomData?.settings?.speakers || []);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const { isSlowMode, timer, startCooldown } = useRateLimit(`qa-${sessionId}`, 30);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

  const fetchQuestions = async () => {
    if (!sessionId) return;
    try {
      const endpoint = user ? `/questions?room_id=${sessionId}` : `/public/questions?room_id=${sessionId}`;
      const response = await api.get(endpoint);
      // Handle potential paginated response from api.js helper
      const data = Array.isArray(response) ? response : (response?.data || []);
      
      setQuestions(prev => {
        // Create a map for existing questions for quick lookup and update
        const existingMap = new Map(prev.map(q => [q.id, q]));
        
        // Update existing or add new
        data.forEach(q => {
          existingMap.set(q.id, q);
        });

        // Convert back to array and sort: 
        // 1. Highlighted (pinned) first
        // 2. Then by date (newest first for root level)
        return Array.from(existingMap.values()).sort((a, b) => {
          if (a.highlighted && !b.highlighted) return -1;
          if (!a.highlighted && b.highlighted) return 1;
          // Newest first for root questions
          return new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now());
        });
      });
      setLastSync(new Date());
      setIsInitialLoading(false);
    } catch (err) {
      console.error('Failed to load questions', err);
      throw err; // Re-throw for usePolling to handle
    }
  };

  const { isSyncing } = usePolling(fetchQuestions, 5000, [sessionId]);

  const handleInputChange = (e) => {
    setNewQuestion(e.target.value);
  };

  const handleSend = async () => {
    if ((sessionState !== 'live' && sessionState !== 'upcoming') || !newQuestion.trim() || isSlowMode || !user) {
      if (isSlowMode) {
        setError(`Slow mode active. Please wait ${timer}s before posting again.`);
        setTimeout(() => setError(''), 3000);
      }
      return;
    }

    startCooldown();

    try {
      const created = await api.post('/questions', {
        text: newQuestion.trim(),
        room_id: sessionId,
        community_id: communityId,
        parent_id: replyingTo?.id,
      });
      // Immediately add to list to avoid waiting for next poll
      setQuestions((prev) => [created, ...prev]);
      setNewQuestion('');
      setError('');
      setReplyingTo(null);
      addToast('Question posted!', 'success');
    } catch (err) {
      console.error('Failed to submit question', err);
      setError(err.message || 'Unable to send question. Please try again.');
      // Session storage will still have the rate limit, but we could clear it if we want to allow retry on error
      // However, usually it's better to keep it to prevent spamming failed requests.
    } finally {
      // No extra finally logic needed for now
    }
  };

  const handleVote = async (id) => {
    if ((sessionState !== 'live' && sessionState !== 'upcoming') || !user) return;
    try {
      const updated = await api.post(`/questions/${id}/vote`);
      setQuestions((prev) => Array.isArray(prev) ? prev.map((q) => q.id === updated.id ? updated : q) : []);
    } catch (err) {
      console.error('Vote failed', err);
    }
  };

  const handleDownvote = async (id) => {
    if ((sessionState !== 'live' && sessionState !== 'upcoming') || !user) return;
    try {
      const updated = await api.post(`/questions/${id}/downvote`);
      setQuestions((prev) => Array.isArray(prev) ? prev.map((q) => q.id === updated.id ? updated : q) : []);
    } catch (err) {
      console.error('Downvote failed', err);
    }
  };

  const updateQuestion = async (id, updates) => {
    if (sessionState === 'archived' || sessionState === 'locked') return;
    try {
      const updated = await api.put(`/questions/${id}`, updates);
      setQuestions((prev) => Array.isArray(prev) ? prev.map((q) => q.id === updated.id ? updated : q) : []);
    } catch (err) {
      console.error('Failed to update question', err);
    }
  };

  const toggleMute = (id, currentMuted) => {
    updateQuestion(id, { is_muted: !currentMuted });
  };

  const toggleDelete = (id, currentDeleted) => {
    updateQuestion(id, { is_deleted: !currentDeleted });
  };

  const handleDelete = async (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Question?',
      description: 'Are you sure you want to delete this question? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/questions/${id}`);
          setQuestions((prev) => prev.filter(q => q.id !== id));
          setConfirmConfig({ isOpen: false });
        } catch (err) {
          console.error('Failed to delete question', err);
        }
      }
    });
  };

  const togglePin = (id, currentHighlighted) => {
    updateQuestion(id, { highlighted: !currentHighlighted });
  };

  const isInteractionDisabled = sessionState !== 'live' && sessionState !== 'upcoming';

  const getVariant = () => {
    const t = theme.type?.toLowerCase();
    if (t === 'mixed') return 'orange';
    if (t === 'poll') return 'indigo';
    if (t === 'brainstorm') return 'green';
    return 'purple';
  };

  const variant = getVariant();

  return (
    <div className="flex flex-col h-full bg-white rounded-3xl border border-gray-100 shadow-xl shadow-brand-indigo/5 overflow-hidden">
      <div className={cn("p-5 border-b border-gray-50 flex flex-col gap-4 md:flex-row items-start md:items-center justify-between bg-gradient-to-br to-white", theme.lightBg)}>
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-2xl", theme.lightBg, theme.text)}>
            <MessageCircle size={20} className="fill-current opacity-20" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 leading-none">Q&A</h3>
            <div className="mt-1.5">
               <LiveSyncIndicator isSyncing={isSyncing} variant={variant} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
           <span className={cn(
             "px-2.5 py-1 rounded-xl text-[10px] font-bold uppercase tracking-[0.15em] shadow-sm",
             sessionState === 'live' ? "bg-red-500 text-white" : "bg-gray-100 text-gray-500"
           )}>
             {sessionState}
           </span>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className={cn("p-4 bg-gradient-to-br to-white border-b border-gray-50", theme.lightBg)}>
          <InteractionTip message="Upvote questions to help the host see what's important" variant={variant} />
          {!user ? (
            <AuthCTA message="Sign in to ask a question or vote." variant={variant} />
          ) : (
            <div className="space-y-2">
              {replyingTo && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className={cn("flex items-center justify-between px-3 py-2 rounded-xl border", theme.lightBg, theme.lightBorder)}
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Reply size={12} className={cn("shrink-0 opacity-60", theme.text)} />
                    <p className={cn("text-[11px] font-medium truncate", theme.text)}>
                      Replying to <span className="font-bold">{replyingTo.author_user?.name || replyingTo.author || 'Guest'}</span>: "{replyingTo.text}"
                    </p>
                  </div>
                  <button onClick={() => setReplyingTo(null)} className={cn("shrink-0 opacity-40 hover:opacity-100 active:scale-95", theme.text)}>
                    <X size={14} />
                  </button>
                </motion.div>
              )}
              <div className="relative">
                <input 
                  type="text" 
                  value={newQuestion}
                  onChange={handleInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  disabled={isInteractionDisabled || isSlowMode}
                  placeholder={
                    sessionState === 'paused' ? "Session is paused..." :
                    sessionState === 'locked' ? "Room is locked (Read-only)" :
                    sessionState === 'archived' ? "Session archived" :
                    sessionState === 'upcoming' ? "Ask a question early..." :
                    isSlowMode ? `Slow mode active (${timer}s)...` : "Ask a question..."
                  }
                  className={cn(
                    "w-full pl-4 pr-12 py-3.5 sm:pl-5 sm:pr-14 sm:py-4 bg-white border-2 border-gray-50 rounded-2xl outline-none font-bold text-sm sm:text-base text-gray-700 transition-all shadow-sm focus:shadow-md",
                    theme.ring,
                    `focus:${theme.border}`,
                    (isInteractionDisabled || isSlowMode) && "opacity-60 cursor-not-allowed"
                  )}
                />
                <button 
                  onClick={handleSend}
                  disabled={isInteractionDisabled || isSlowMode}
                  className={cn(
                    "absolute right-1.5 top-1/2 -translate-y-1/2 p-2.5 sm:p-3 rounded-xl transition-all active:scale-95",
                    (isInteractionDisabled || isSlowMode) ? "bg-gray-200 text-gray-400" : cn("text-white shadow-lg", theme.bg, theme.shadow, theme.hover)
                  )}
                >
                  <Send size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 p-4 scrollbar-thin">
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-3 py-2 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2"
            >
              <AlertTriangle size={14} className="text-red-500" />
              <p className="text-[11px] font-bold text-red-600">{error}</p>
              <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600 active:scale-95">
                <X size={14} />
              </button>
            </motion.div>
          )}
        {Array.isArray(questions) && questions.length === 0 && !isInitialLoading ? (
          <EmptyState 
            icon={MessageCircle}
            title="No questions yet"
            description="Be the first to ask something and get the conversation started!"
            variant={variant}
          />
        ) : (
          Array.isArray(questions) && questions.filter(q => !q.parent_id).map((q) => {
            const replies = questions.filter(r => String(r.parent_id) === String(q.id));
            return (
              q.is_deleted ? (
                <SoftDeletePlaceholder 
                  key={q.id} 
                  type="question" 
                  onRestore={() => toggleDelete(q.id, q.is_deleted)} 
                  sessionId={sessionId}
                  communityId={communityId}
                  session={roomData}
                />
              ) : (
                <div key={q.id} className="space-y-2">
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "p-4 rounded-2xl border transition-all group hover:shadow-2xl hover:-translate-y-0.5",
                      q.highlighted ? cn("shadow-xl", theme.lightBg, theme.border, theme.shadow) : cn("border-slate-100 bg-white shadow-sm", `hover:${theme.lightBg}`, `hover:${theme.lightBorder}`)
                    )}
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-2 sm:gap-3 mb-3">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className={cn("w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl p-0.5 border overflow-hidden shadow-sm shrink-0", theme.lightBg, theme.lightBorder)}>
                          <img src={q.author_user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${q.author_user?.name || q.author || 'guest'}`} alt="avatar" className="w-full h-full object-cover rounded-lg sm:rounded-xl" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-0.5">
                            <p className={cn("text-xs sm:text-sm font-bold text-gray-900 truncate max-w-[80px] xs:max-w-[120px] sm:max-w-none", q.is_muted && "text-gray-400 line-through")}>{q.author_user?.name || q.author || 'Guest'}</p>
                            {(String(q.author_id) === String(roomData?.owner_id) || String(q.author_id) === String(roomData?.host_id)) && (
                              <span className={cn("text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-xl border uppercase tracking-[0.15em]", theme.text, theme.lightBg, theme.lightBorder)}>Host</span>
                            )}
                            {sessionSpeakers.includes(q.author_user?.name || q.author) && (
                              <span className={cn("text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-xl border uppercase tracking-[0.15em] bg-orange-50 text-orange-600 border-orange-100")}>Speaker</span>
                            )}
                            {q.is_muted && <span className="text-[8px] sm:text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 sm:px-2 py-0.5 rounded-xl uppercase tracking-[0.15em]">Muted</span>}
                            {q.highlighted && <Pin size={10} className={cn("sm:w-3 sm:h-3 fill-current shrink-0", theme.text)} />}
                          </div>
                          <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-[0.15em] opacity-80">{q.created_at ? new Date(q.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1.5 sm:gap-2 ml-auto sm:ml-0">
                        <ContentModerationActions 
                          onDelete={() => String(q.author_id) === String(user?.id) ? handleDelete(q.id) : toggleDelete(q.id, q.is_deleted)} 
                          onPin={() => togglePin(q.id, q.highlighted)}
                          onMute={() => toggleMute(q.id, q.is_muted)}
                          isPinned={q.highlighted}
                          isMuted={q.is_muted}
                          sessionId={sessionId}
                          communityId={communityId}
                          isAuthor={String(q.author_id) === String(user?.id)}
                          session={roomData}
                        />
                        <button 
                          onClick={() => setReplyingTo(q)}
                          disabled={isInteractionDisabled || !user}
                          className={cn("flex items-center gap-1 px-1.5 sm:px-2 py-1 sm:py-1.5 rounded-lg text-gray-400 transition-colors", `hover:${theme.lightBg}`, `hover:${theme.text}`)}
                          title="Reply"
                        >
                          <Reply size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span className="hidden xs:inline text-[9px] sm:text-[10px] font-bold uppercase tracking-wider">Reply</span>
                        </button>
                        <div className={cn("flex items-center rounded-lg sm:rounded-xl overflow-hidden border", theme.lightBg, theme.lightBorder)}>
                          <button 
                            onClick={() => handleVote(q.id)}
                            disabled={isInteractionDisabled || !user}
                            className={cn(
                              "flex items-center gap-1 px-2 sm:px-2.5 py-1 sm:py-1.5 font-bold text-[10px] sm:text-xs transition-all border-r",
                              theme.lightBorder,
                              q.votes_rel?.some(v => v.user_id === user?.id && v.type === 'upvote') 
                                ? cn("text-white", theme.bg) 
                                : cn("text-gray-400 hover:opacity-80", theme.lightBg),
                              (isInteractionDisabled || !user) && "cursor-not-allowed opacity-50"
                            )}
                          >
                            <ThumbsUp size={10} className={cn("sm:w-3 sm:h-3", q.votes_rel?.some(v => v.user_id === user?.id && v.type === 'upvote') ? "fill-current" : "")} />
                            <span className="tabular-nums">{q.votes_count ?? q.votes_sum ?? q.votes_total ?? q.votes ?? 0}</span>
                          </button>
                          <button 
                            onClick={() => handleDownvote(q.id)}
                            disabled={isInteractionDisabled || !user}
                            className={cn(
                              "flex items-center gap-1 px-2 sm:px-2.5 py-1 sm:py-1.5 font-bold text-[10px] sm:text-xs transition-all",
                              q.votes_rel?.some(v => v.user_id === user?.id && v.type === 'downvote')
                                ? "bg-red-500 text-white" 
                                : "text-gray-400 hover:bg-red-50 hover:text-red-500",
                              (isInteractionDisabled || !user) && "cursor-not-allowed opacity-50"
                            )}
                          >
                            <ThumbsDown size={10} className={cn("sm:w-3 sm:h-3", q.votes_rel?.some(v => v.user_id === user?.id && v.type === 'downvote') ? "fill-current" : "")} />
                            <span className="tabular-nums">{q.downvotes || 0}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 font-medium text-sm leading-relaxed pl-0.5 break-words">{q.text}</p>
                  </motion.div>

                  {/* Replies */}
                  {replies.length > 0 && (
                    <div className={cn("ml-2 xs:ml-4 sm:ml-8 space-y-2 border-l-2 pl-3 sm:pl-4", theme.lightBorder)}>
                      {replies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map(reply => (
                        <motion.div 
                          key={reply.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="p-3 bg-gray-50/50 rounded-2xl border border-gray-100 group"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-2">
                              <div className={cn("w-6 h-6 rounded-lg overflow-hidden", theme.lightBg)}>
                                <img src={reply.author_user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${reply.author_user?.name || reply.author || 'guest'}`} alt="avatar" />
                              </div>
                              <p className="text-xs font-bold text-gray-900">{reply.author_user?.name || reply.author || 'Guest'}</p>
                              {(String(reply.author_id) === String(roomData?.owner_id) || String(reply.author_id) === String(roomData?.host_id)) && (
                                <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded border uppercase tracking-widest", theme.text, theme.lightBg, theme.lightBorder)}>Host</span>
                              )}
                              {sessionSpeakers.includes(reply.author_user?.name || reply.author) && (
                                <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded border uppercase tracking-widest bg-orange-50 text-orange-600 border-orange-100")}>Speaker</span>
                              )}
                              {String(reply.author_id) === String(q.author_id) && (
                                <span className={cn("text-[8px] font-bold px-1 py-0.5 rounded border uppercase tracking-widest", theme.text, theme.lightBg, theme.lightBorder)}>Author</span>
                              )}
                              <p className="text-[10px] text-gray-400 font-bold uppercase">{reply.created_at ? new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}</p>
                            </div>
                            <div className="flex items-center gap-1 bg-white rounded-lg px-1.5 py-0.5 border border-gray-100">
                               <button 
                                onClick={() => handleVote(reply.id)}
                                className={cn(
                                  "p-1 transition-colors", 
                                  reply.votes_rel?.some(v => v.user_id === user?.id && v.type === 'upvote') ? theme.text : "text-gray-400 hover:text-gray-600"
                                )}
                               >
                                 <ThumbsUp size={10} className={reply.votes_rel?.some(v => v.user_id === user?.id && v.type === 'upvote') ? "fill-current" : ""} />
                               </button>
                               <span className="text-[10px] font-bold text-gray-500 mr-1">{reply.votes || 0}</span>
                               <button 
                                onClick={() => handleDownvote(reply.id)}
                                className={cn(
                                  "p-1 transition-colors border-l border-gray-100 pl-1",
                                  reply.votes_rel?.some(v => v.user_id === user?.id && v.type === 'downvote') ? "text-red-500" : "text-gray-400 hover:text-red-500"
                                )}
                               >
                                 <ThumbsDown size={10} className={reply.votes_rel?.some(v => v.user_id === user?.id && v.type === 'downvote') ? "fill-current" : ""} />
                               </button>
                               <span className="text-[10px] font-bold text-gray-500">{reply.downvotes || 0}</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 font-medium break-words">{reply.text}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )
            );
          })
        )}
        </div>
      </div>
      <ConfirmModal 
        {...confirmConfig}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default LiveQA;
